import { CLOB_API_URL_V2, POLYGON_CHAIN_ID, POLY_BUILDER_CODE } from '@/constants/polymarket'
import {
	createDepositWalletClobClient,
	deriveDepositWalletAddress,
} from '@/lib/wallets/polymarket-deposit'
import { captureApiError } from '@/utils/sentry-helpers'
import { type TradingSession, loadSession, saveSession } from '@/utils/session'
import { ClobClient } from '@polymarket/clob-client-v2'
import type { Address, WalletClient } from 'viem'

export type PolymarketApiCredentials = NonNullable<TradingSession['apiCredentials']>

/**
 * Returns valid Polymarket CLOB API credentials for the given EOA: cached
 * if loadSession has them, otherwise derived (free) or created (charges
 * a tx) via the CLOB API.
 */
export async function getOrCreatePolymarketApiCredentials({
	eoaAddress,
	ethersSigner,
}: {
	eoaAddress: string
	ethersSigner: unknown
}): Promise<PolymarketApiCredentials> {
	const stored = loadSession(eoaAddress)
	const cached = stored?.apiCredentials
	if (cached?.key && cached.secret && cached.passphrase) return cached

	const tempClient = new ClobClient({
		host: CLOB_API_URL_V2,
		chain: POLYGON_CHAIN_ID,
		// biome-ignore lint/suspicious/noExplicitAny: ClobClient expects ethers v5 Signer
		signer: ethersSigner as any,
	})

	const derived = await tempClient.deriveApiKey().catch(() => undefined)
	if (derived?.key && derived.secret && derived.passphrase) return derived

	return tempClient.createApiKey()
}

/**
 * Construct an authenticated V2 ClobClient for the given funder. Order
 * signing uses the on-order builderCode field, not HMAC. signatureType=2
 * = POLY_GNOSIS_SAFE (unchanged in V2).
 */
export function createAuthenticatedPolymarketClobClient({
	ethersSigner,
	apiCreds,
	funderAddress,
}: {
	ethersSigner: unknown
	apiCreds: PolymarketApiCredentials
	funderAddress: string
}): ClobClient {
	return new ClobClient({
		host: CLOB_API_URL_V2,
		chain: POLYGON_CHAIN_ID,
		// biome-ignore lint/suspicious/noExplicitAny: ClobClient expects ethers v5 Signer
		signer: ethersSigner as any,
		creds: apiCreds,
		signatureType: 2,
		funderAddress,
		useServerTime: false,
		builderConfig: POLY_BUILDER_CODE ? { builderCode: POLY_BUILDER_CODE } : undefined,
	})
}

/**
 * Persist a Polymarket trading session. The localStorage schema still uses
 * `safeAddress` for backward compatibility — renaming the on-disk shape
 * happens with the V2 custom-wallet migration, not here.
 */
export function savePolymarketTradingSession({
	eoaAddress,
	funderAddress,
	apiCredentials,
}: {
	eoaAddress: string
	funderAddress: string
	apiCredentials: PolymarketApiCredentials
}): void {
	saveSession(eoaAddress, {
		eoaAddress,
		safeAddress: funderAddress,
		isSafeDeployed: true,
		hasApiCredentials: true,
		hasApprovals: true,
		apiCredentials,
		lastChecked: Date.now(),
	})
}

/**
 * Construct an authenticated V2 ClobClient, dispatching on user kind.
 *
 *   polymarketSafe NOT NULL → POLY_GNOSIS_SAFE (signatureType=2), funder = Safe address.
 *   polymarketSafe NULL     → POLY_1271 (signatureType=3), funder = deposit wallet address.
 */
export function createPolymarketClobClient({
	polymarketSafe,
	funderAddress,
	walletKind,
	eoaAddress,
	ethersSigner,
	walletClient,
	apiCreds,
}: {
	polymarketSafe: string | null
	funderAddress?: string
	walletKind?: 'safe' | 'deposit'
	eoaAddress: string
	ethersSigner: unknown
	walletClient: WalletClient | null
	apiCreds: PolymarketApiCredentials
}): ClobClient {
	// walletKind (resolved by ensurePolymarketWalletReady) is authoritative when
	// present: a legacy user who switched to the deposit wallet has walletKind
	// 'deposit' with polymarketSafe still non-null. Only fall back to the
	// polymarketSafe heuristic when walletKind wasn't provided.
	if (walletKind === 'safe' || (walletKind === undefined && polymarketSafe)) {
		return createAuthenticatedPolymarketClobClient({
			ethersSigner,
			apiCreds,
			funderAddress: funderAddress ?? (polymarketSafe as string),
		})
	}

	// Deposit-wallet path: use viem WalletClient + POLY_1271 signature type.
	if (!walletClient) {
		throw new Error('WalletClient required for deposit-wallet ClobClient')
	}
	try {
		const depositWalletAddress = (funderAddress ??
			deriveDepositWalletAddress(eoaAddress as Address)) as Address
		return createDepositWalletClobClient({
			walletClient,
			depositWalletAddress,
			creds: apiCreds,
		})
	} catch (err) {
		captureApiError(err, {
			endpoint: 'polymarket_deposit_session',
			tags: { category: 'polymarket-deposit', flow: 'session' },
			extra: { eoaAddress, userId: undefined },
		})
		throw err
	}
}
